import React, {
    useEffect,
    useCallback,
} from 'react';
import {useParams} from 'react-router-dom';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useDebouncedCallback} from 'use-debounce';

import {
    ApTimelineThumbnails,
    ApTimelineThumbnail,
} from '@accurate-player/accurate-player-components-external';

import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {
    SetThumbnails,
    ResetThumbnails,
} from './Redux/ThumbnailsActions';
import Thumbnails from './Thumbnails';
import {
    ThumbnailMarker,
    OnTimelineMarkerClick,
} from '../TimelineTypes';

export type ThumbnailsContainerProps = {
    item: Item;
    setThumbnails: SetThumbnails;
    resetThumbnails: ResetThumbnails;
    markers: ThumbnailMarker[];
    onThumbnailClick: OnTimelineMarkerClick;
}

export interface SetThumbnailsParams {
    from: number;
    to: number;
    count: number;
}

const handleThumbnailsChange = curry((thumbnailMarkers: ThumbnailMarker[], node: ApTimelineThumbnail) => {
    if (node) {
        node.data = thumbnailMarkers;
    }
});

const handleThumbnailContainerChange = curry((onThumbnailClick: OnTimelineMarkerClick, node: ApTimelineThumbnails) => {
    if (node) {
        node.apMarkerClick = onThumbnailClick;
        node.height = 32;
    }
});

const useThumbnailsRef = (props: ThumbnailsContainerProps) => {
    const {markers} = props;
    const onThumbnailChange = handleThumbnailsChange(markers);
    const setRef = useCallback(onThumbnailChange, [markers]);
    
    return [setRef];
};

const useThumbnailsContainerRef = (props: ThumbnailsContainerProps) => {
    const {onThumbnailClick} = props;
    const onThumbnailChange = handleThumbnailContainerChange(onThumbnailClick);
    const setRef = useCallback(onThumbnailChange, [onThumbnailClick]);
    
    return [setRef];
};

export const handleMount = wrap((props: ThumbnailsContainerProps) => {
    const {
        setThumbnails,
        resetThumbnails,
        item,
    } = props;
    const {thumbnails} = item;
    const {duration} = item.video;
    const params = {
        from: 0,
        to: duration,
        count: 200,
    };
    
    setThumbnails(thumbnails, params);
    
    return resetThumbnails;
});

export const handleZoomChange = curry((debouncedFunction: (params: SetThumbnailsParams) => void, oldZoomChange: (min: number, max: number) => void, min: number, max: number) => {
    const params = {
        from: Math.floor(min),
        to: Math.floor(max),
        count: 50,
    };
    debouncedFunction(params);
    
    oldZoomChange(min, max);
});

export const debouncedThumbnailsCallback = curry((setThumbnails: SetThumbnails, thumbnails: string, params: SetThumbnailsParams) => {
    setThumbnails(thumbnails, params);
});

export default function ThumbnailsContainer(props: ThumbnailsContainerProps) {
    const {
        item,
        setThumbnails,
    } = props;
    const {thumbnails} = item;
    const {id} = useParams<{ id: string }>();
    const [setThumbnailsRef] = useThumbnailsRef(props);
    const [setThumbnailsContainerRef] = useThumbnailsContainerRef(props);
    const [debouncedFunction] = useDebouncedCallback(debouncedThumbnailsCallback(setThumbnails, thumbnails), 1000);
    
    const timeline: any | null = document.querySelector('ap-timeline-basic') || {};
    
    const oldZoomChange = timeline.onZoomChange;
    timeline.onZoomChange = handleZoomChange(debouncedFunction, oldZoomChange);
    
    const onMount = handleMount(props, id);
    useEffect(onMount, []);
    
    return (
        <Thumbnails
            setThumbnailsRef={setThumbnailsRef}
            setThumbnailsContainerRef={setThumbnailsContainerRef}
        />
    );
}
